import {DestroyRef, Injectable} from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,
  delay,
  filter,
  first,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  LookUpDTO,
  LookUpObject,
  MotorQuoteAgencyDTO,
  MotorQuoteChargePlanDTO,
  MotorQuoteDTO,
  MotorQuoteDeliveryFieldsDTO,
  MotorQuoteDeliveryFieldsEmailDTO,
  MotorQuoteInputDTO,
  MotorQuoteVehicleFieldSetDTO,
  MotorQuoteVehicleFieldsDTO,
  MotorQuoteWarrantyDTO,
} from '../../models/MotorQuoteDTO.interfaces';
import {SessionStorageService} from '../session-storage/session-storage.service';

import {ComponentType} from '@angular/cdk/portal';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router, Scroll} from '@angular/router';
import {NxModalRef} from '@aposin/ng-aquila/modal';
import {environment} from '../../../environments/environment';
import {FormReturn} from '../../../libs/az-forms/interfaces/api-object-formatted';
import {FormParts, FormSelectOption} from '../../../libs/az-forms/interfaces/forms/form-element';
import {DynamicComponentObject} from '../../../libs/dynamic-components/interfaces/dynamic-component-object';
import {ButtonComponent} from '../../components/button/button.component';
import {IconLinkComponent} from '../../components/icon-link/icon-link.component';
import {ModalInputsButtonsComponent} from '../../components/modals/modal-inputs-buttons/modal-inputs-buttons.component';
import {PriceDiscountComponent} from '../../components/price-discount/price-discount.component';
import {StickyContentComponent} from '../../components/sticky-content/sticky-content.component';
import {StickyInformationComponent} from '../../components/sticky-box/components/sticky-information/sticky-information.component';
import {TitleComponent} from '../../components/title/title.component';
import {Endpoints, GeoservicesEndpoints} from '../../enums/endpoints.enum';
import {FieldsUuidEnum} from '../../enums/fields-uuid.enum';
import {MotorQuoteConfigurations} from '../../enums/motor-quote-configurations';
import {RoutingEnum} from '../../enums/routing.enum';
import {LabelValue} from '../../models/stepData.model';
import {
  ButtonComponentData,
  ContentsComponent,
  IconLinkComponentData,
  PriceDiscountComponentData,
  StickyInformationComponentData,
  TitleComponentData,
} from '../../models/sticky-simple-componet.model';
import {AnalyticsService} from '../analytics/analytics.service';
import {ApiUrlService} from '../apiUrl/api-url.service';
import {ContentService} from '../content/content.service';
import {HttpService} from '../http/http.service';
import {RecaptchaHelperService} from '../recaptcha-helper/recaptcha-helper.service';
import {ContactsMapService} from '../contacts-map/contacts-map.service';

@Injectable({
  providedIn: 'root',
})
export class StepResponseService {
  private activePlans: MotorQuoteChargePlanDTO[] = [];
  private lookUpObject: LookUpObject = {};

  // Inizializza con null
  public _response$: BehaviorSubject<MotorQuoteDTO | null> =
    new BehaviorSubject<MotorQuoteDTO | null>(null);
  response$ = this._response$.asObservable();

  public _allianzPayCode$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  allianzPayCode$ = this._allianzPayCode$.asObservable();

  public _initialResponse$: BehaviorSubject<MotorQuoteDTO | null> =
    new BehaviorSubject<MotorQuoteDTO | null>(null);
  initialResponse$ = this._initialResponse$.asObservable();

  public _looksup$: BehaviorSubject<LookUpDTO[] | null> = new BehaviorSubject<LookUpDTO[] | null>(
    null,
  );
  looksup$ = this._looksup$.asObservable();

  private agencyDataSubject = new BehaviorSubject<MotorQuoteAgencyDTO | null>(null);
  agencyData$ = this.agencyDataSubject.asObservable();

  constructor(
    private contentService: ContentService,
    private httpService: HttpService,
    private sessionStorageService: SessionStorageService,
    private apiUrlService: ApiUrlService,
    private recaptchaHelperService: RecaptchaHelperService,
    private analyticsService: AnalyticsService,
    private contactMapService: ContactsMapService,
    private router: Router,
    private destroyRef$: DestroyRef,
  ) {}

  public set response(configuration: MotorQuoteDTO) {
    this._response$.next(configuration);
    this.sessionStorageService.set('fq-response-dati-form-calcola-targa', configuration);
  }

  public get response(): MotorQuoteDTO | null {
    if (!this._response$.value) {
      this._response$.next(this.sessionStorageService.get('fq-response-dati-form-calcola-targa'));
    }
    return this._response$.value;
  }

  get agencyData(): MotorQuoteAgencyDTO | null {
    return this.agencyDataSubject.value;
  }

  set agencyData(data: MotorQuoteAgencyDTO | null) {
    this.agencyDataSubject.next(data);
  }

  public set allianzPayCode(code: string) {
    this._allianzPayCode$.next(code);
    this.sessionStorageService.set('fq-allianzPayCode', code);
  }

  get allianzPayCode(): string | null {
    const value = this._allianzPayCode$.getValue();
    return value.length > 0 ? value : null;
  }

  public set initialResponse(configuration: MotorQuoteDTO) {
    this._response$.next(configuration);
    this.sessionStorageService.set('fq-response-dati-form-calcola-targa', configuration);
  }

  public get initialResponse(): MotorQuoteDTO | null {
    if (!this._initialResponse$.value) {
      this._initialResponse$.next(
        this.sessionStorageService.get('fq-response-dati-form-calcola-targa'),
      );
    }
    return this._initialResponse$.value;
  }

  resetConfig() {
    const url = this.apiUrlService.getUrl(Endpoints.RESET_CONFIGURATION);

    this.httpService
      .get(url)
      .pipe()
      .subscribe((data: MotorQuoteDTO) => {
        this.response = data;
      });
    this.analyticsService.sendAnalytics('Reset response');
  }

  public set looksup(configuration: LookUpDTO[]) {
    this._looksup$.next(configuration);
  }

  public get looksup(): LookUpDTO[] | null {
    if (!this._looksup$.value) {
    }
    return this._looksup$.value;
  }

  manageData(obj: Record<string, unknown>) {
    const conversionValues: Record<string, string> = {
      p: 'vehiclePlate',
      b: 'insuredBirthday',
      e: 'insuredEmail',
      a: 'agency',
      r: 'referral',
      py: 'privacy',
    };
    const initData = Object.keys(obj).reduce((retObj: Record<string, unknown>, el) => {
      const newKey = conversionValues[el] ?? null;
      if (newKey) {
        retObj[newKey] = obj[el];
      }
      return retObj;
    }, {});
    return this.sendData({
      captcha: null,
      data: initData,
    });
  }

  sendData(response: FormReturn) {
    const queryParams: Record<string, unknown> = this.router.parseUrl(this.router.url).queryParams;
    if (queryParams?.['a']) {
      response.data.agency = queryParams['a'];
    }
    return this.initData(response).pipe(
      take(1),
      catchError((error) => {
        console.error(error);
        this.router.navigate([RoutingEnum.QuotationError], {skipLocationChange: true});
        return EMPTY;
      }),
      tap(() => {
        this.router.navigate([RoutingEnum.Step1]);
      }),
    );
  }

  initData(response: FormReturn) {
    const url = this.apiUrlService.getUrl(Endpoints.INIT);

    return this.httpService.post(url, response.data).pipe(
      tap((data: MotorQuoteDTO) => {
        this.storeData(data);
        this.checkAndAddAgency(data);
      }),
    );
  }
  storeData(data: MotorQuoteDTO) {
    this.response = data;
    this.initialResponse = data;
    this._response$.next(data);
    this.sessionStorageService.set('fq-response-dati-form-calcola-targa', data);
  }

  checkAndAddAgency(data: MotorQuoteDTO) {
    if (data.response.configuration.agency.cod) {
      const agencyByCod$ = this.contactMapService.getAgencyByCod(
        data.response.configuration.agency.cod,
      );

      const url = this.apiUrlService.getUrl(Endpoints.SET_AGENCY);
      agencyByCod$
        .pipe(
          switchMap((el) => {
            return this.httpService.post(url, {agency: el}).pipe(
              tap((res) => {
                this.storeData(res);
              }),
            );
          }),
        )
        .subscribe();
    }
  }

  getCurrentPlans(data = this.response, force = false): MotorQuoteChargePlanDTO[] {
    if (force || this.activePlans.length === 0) {
      this.activePlans = data?.response.plans.filter((plan) => plan.active) ?? [];
    }
    return this.activePlans;
  }

  getVehicleVersions(vehicleData: MotorQuoteVehicleFieldsDTO): FormSelectOption[] {
    const variant = vehicleData.variant;
    return (
      variant.widget.options?.map((option) => ({
        value: option.value,
        text: option.label,
      })) ?? []
    );
  }

  getCurrentPlan(data = this.response, force = false): MotorQuoteChargePlanDTO {
    const plans = this.getCurrentPlans(data, force);
    return plans[0];
  }

  getWarranties(data: MotorQuoteDTO) {
    const warrantiesList = data.response.configuration.warranties;
    return warrantiesList;
  }

  // getVehicleInfo(data: MotorQuoteDTO) {
  //   const vehicleData = data.response.insuranceData.vehicle.fieldset[0].fields;
  //   const title: (keyof MotorQuoteVehicleFieldsDTO)[] = ['plate', 'manufacturer', 'model'];
  //   const titleObj = title.reduce(
  //     (obj: Record<string, string>, el: keyof MotorQuoteVehicleFieldsDTO) => {
  //       obj[el] = vehicleData[el].displayValue?.toString() ?? '';
  //       return obj;
  //     },
  //     {},
  //   ) as {
  //     plate: string;
  //     manufacturer: string;
  //     model: string;
  //   };

  //   return `${titleObj.plate} - ${titleObj.manufacturer}, ${titleObj.model}`;
  // }

  createPopoverID(
    lookupScope: MotorQuoteConfigurations,
    allPlans: string = this.getCurrentPlans()
      .map((plan) => plan.uuid)
      .join('-'),
    uuidScope: string,
    uuidField?: string,
  ) {
    const arr = ['info', lookupScope, allPlans, uuidScope];

    if (uuidField) {
      arr.push('fields', uuidField);
    }

    return arr.join('-');
  }

  createPopoverIDInsurance(
    lookupScope: MotorQuoteConfigurations,
    uuidScope: string,
    uuidField?: string,
  ) {
    const arr = ['info', lookupScope, uuidScope];

    if (uuidField) {
      arr.push('fields', uuidField);
    }

    return arr.join('-');
  }

  getPopoverListWarranties(
    warranties: MotorQuoteWarrantyDTO[],
    lookupScope: MotorQuoteConfigurations,
    plans = this.getCurrentPlans(),
  ): string[] {
    const allPlans = plans.map((el) => el.uuid).join('-');

    const popoverIdList = warranties.reduce((arr: string[], el) => {
      const basePopover = this.createPopoverID(lookupScope, allPlans, el.uuid);
      arr.push(basePopover);
      if (el.fields && el.fields.length > 0) {
        const fieldsId = el.fields.map((res) => {
          return this.createPopoverID(lookupScope, allPlans, el.uuid, res.uuid);
        });
        arr.push(...fieldsId);
      }
      return arr;
    }, []);
    return popoverIdList;
  }

  // populateStickyData(data: MotorQuoteDTO, componentsList: DynamicComponentObject[]) {
  //   const titleComponent = this.filterComponentsByType<TitleComponentData>(
  //     TitleComponent,
  //     componentsList,
  //   );
  //   const priceDiscountComponent = this.filterComponentsByType<PriceDiscountComponentData>(
  //     PriceDiscountComponent,
  //     componentsList,
  //   );
  //   const stickyContentComponent = this.filterComponentsByType<ContentsComponent>(
  //     StickyContentComponent,
  //     componentsList,
  //   );
  //   const stickyInformationComponent = this.filterComponentsByType<StickyInformationComponentData>(
  //     StickyInformationComponent,
  //     componentsList,
  //   );
  //   const buttonComponent = this.filterComponentsByType<ButtonComponentData>(
  //     ButtonComponent,
  //     componentsList,
  //   );
  //   const iconLinkComponent = this.filterComponentsByType<IconLinkComponentData>(
  //     IconLinkComponent,
  //     componentsList,
  //   );

  //   const activePlan: MotorQuoteChargePlanDTO = this.getCurrentPlan(
  //     data,
  //   ) as MotorQuoteChargePlanDTO;
  //   const labelPeriodPayment = this.getLabelFromValue(data.response.configuration.payment);
  //   const agencyData = data.response.configuration.agency;

  //   if (priceDiscountComponent) {
  //     if (activePlan.discount !== 0) {
  //       priceDiscountComponent.data.warrantyOldPrice = activePlan.totalPrice.toString();
  //     }
  //     priceDiscountComponent.data.warrantyPrice = activePlan.price.toString();
  //   }

  //   if (stickyContentComponent) {
  //     if (stickyContentComponent.data.periodPayment) {
  //       stickyContentComponent.data.periodPayment.period = labelPeriodPayment;
  //     }
  //     if (activePlan.discount > 0) {
  //       if (stickyContentComponent.data.discount) {
  //         stickyContentComponent.data.discount.value = activePlan.discount;
  //       }
  //     }

  //     if (agencyData.name !== '') {
  //       if (stickyContentComponent.data.agencyValue) {
  //         stickyContentComponent.data.agencyValue.agency = agencyData.name ?? '';
  //       }
  //     }
  //   }
  // }

  filterComponentsByType<T>(
    componentType: ComponentType<unknown>,
    data: DynamicComponentObject[],
  ): DynamicComponentObject<T> | undefined {
    return data.find((item) => item.component === componentType) as
      | DynamicComponentObject<T>
      | undefined;
  }

  // getLabelFromValue(paymentField: MotorQuoteInputDTO): string {
  //   try {
  //     const {value, widget} = paymentField;

  //     if (!widget || !Array.isArray(widget.options)) {
  //       console.warn('Widget o options non validi in paymentField');
  //       return value ?? ''; // Restituisce il value come fallback
  //     }

  //     const matchingOption = widget.options.find((option) => option.value === value);

  //     if (matchingOption && typeof matchingOption.label === 'string') {
  //       return matchingOption.label;
  //     } else {
  //       console.warn(`Nessuna opzione trovata per il valore "${value}"`);
  //       return value ?? ''; // Restituisce il value come fallback
  //     }
  //   } catch (error) {
  //     console.error('Errore nel processare paymentField:', error);
  //     return paymentField.value || 'Valore non disponibile';
  //   }
  // }

  getPopoverListInsurance(
    insuranceData: MotorQuoteVehicleFieldSetDTO,
    lookupScope: MotorQuoteConfigurations,
    plans = this.getCurrentPlans(),
  ): string[] {
    const allPlans = plans.join('-');
    const popoverIdList: string[] = [];

    const basePopover = this.createPopoverIDInsurance(lookupScope, insuranceData.uuid);
    popoverIdList.push(basePopover);

    if (insuranceData.fields) {
      const fieldsId = Object.values(insuranceData.fields).map((res) => {
        return this.createPopoverIDInsurance(lookupScope, insuranceData.uuid, res.uuid);
      });
      popoverIdList.push(...fieldsId);
    }

    return popoverIdList;
  }
  /**
   * This method help us to obtain {code/s} for lookup API
   * @param lookupScope
   * @param plans
   * @param uuidScope
   * @param fields
   * @param SpecificUuid
   */
  setUpPopsOver(
    lookupScope: string,
    plans: MotorQuoteChargePlanDTO[],
    uuidScope: string,
    fields?: MotorQuoteInputDTO[],
  ): string[] | string {
    // https://docs.google.com/document/d/16S8hFVsjEG4FXAxjTKPxTX6JljjkjCndp1IcDYgH1hg/edit
    // popsOver format: 'info'-lookupScope-activePlansUuid-uuidContestWarranty-'fields'-fieldUuid”

    const activePlanUUIDs = plans.filter((plan) => plan.active).map((plan) => plan.uuid);
    let codesParameter: string[] | string = [];

    if (fields && fields.length > 1) {
      codesParameter = fields.map(
        (field) =>
          `info-${lookupScope}-${activePlanUUIDs.join('-')}-${uuidScope}-fields-${field.uuid}`,
      );
    } else {
      codesParameter = `info-${lookupScope}-${activePlanUUIDs.join('-')}-${uuidScope}`;
    }
    return codesParameter;
  }

  getLookups(lookupsCode: string[]): Observable<LookUpObject> {
    const reduceList = (codeList: string[]) => {
      return codeList.reduce((obj: Record<string, string>, el: string) => {
        obj[el] = this.lookUpObject[el];
        return obj;
      }, {});
    };

    const filteredCodes = lookupsCode.filter((el) => {
      return !this.lookUpObject[el];
    });
    if (filteredCodes.length === 0) {
      return of(reduceList(lookupsCode));
    }

    const url = this.apiUrlService.getUrl(Endpoints.LOOKUP_JSON);
    return this.httpService.post(url, filteredCodes).pipe(
      map((data: LookUpDTO[]) => {
        return (data ?? []).reduce((obj: LookUpObject, el: LookUpDTO) => {
          obj[el.code] = el.description;
          return obj;
        }, {});
      }),
      map((data: LookUpObject) => {
        this.lookUpObject = {...this.lookUpObject, ...data};
        return reduceList(lookupsCode);
        // this.looksup = data;
      }),
    );
  }

  getLookUp(lookupCode: string): Observable<string> {
    if (this.lookUpObject[lookupCode]) {
      return of(this.lookUpObject[lookupCode]);
    }

    return this.getLookups([lookupCode]).pipe(map((lookUpObject) => lookUpObject[lookupCode]));
  }

  getLookupAsync(lookupCode: string) {
    const popoup = this.lookUpObject[lookupCode];
    return popoup;
  }

  updateWarranty(uuid: string, operationValue: 'ON' | 'OFF') {
    const url =
      this.apiUrlService.getUrl(Endpoints.WARRANTY_SELECTION) +
      '/' +
      uuid +
      '?operation=' +
      operationValue;

    this.httpService
      .post(url, {})
      .pipe()
      .subscribe((data: MotorQuoteDTO) => {
        this.response = data;
      });
    this.analyticsService.sendAnalytics(
      'toggle warranty ' + uuid + ' valore inviato: ' + operationValue,
    );
  }

  updatePartner(newvalue: string) {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_CONFIGURATION);
    const data = {
      fieldId: FieldsUuidEnum.PARTNERS,
      value: newvalue,
    };
    this.httpService
      .post(url, data)
      .pipe()
      .subscribe((data: MotorQuoteDTO) => {
        this.response = data;
      });
    this.analyticsService.sendAnalytics('select new partner ' + newvalue);
  }

  updatePaymentPeriod(newvalue: string) {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_CONFIGURATION);
    const data = {
      fieldId: FieldsUuidEnum.PAYMENT,
      value: newvalue,
    };
    this.httpService
      .post(url, data)
      .pipe()
      .subscribe((data: MotorQuoteDTO) => {
        this.response = data;
      });
    this.analyticsService.sendAnalytics('switch period payment' + newvalue);
  }

  getCodeAllianzPay(email: string) {
    //Endpoints.SAVE
    const url = this.apiUrlService.getUrl(Endpoints.SEND) + '?email=' + email + '&subscribe=true';

    return this.httpService.post(url, {}).pipe(
      tap((data: MotorQuoteDTO) => {
        this.response = data;
        this.allianzPayCode = this.response.response.ticket ?? '';
        this.analyticsService.sendAnalytics('allianzPay code: ' + this.response.response.ticket);
      }),
    );
  }

  updateTrailerOption(newvalue: string) {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_CONFIGURATION);
    const data = {
      fieldId: 'TRAILER',
      value: newvalue,
    };
    this.httpService
      .post(url, data)
      .pipe()
      .subscribe((data: MotorQuoteDTO) => {
        this.response = data;
      });
    this.analyticsService.sendAnalytics('switch trailer option' + newvalue);
  }

  updateField(
    changedFields: {key: string; value: unknown}[],
    warranty: MotorQuoteWarrantyDTO,
  ): Observable<MotorQuoteDTO[]> {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_FIELD);
    const arrCalls = changedFields.map((el) => {
      const data = {
        warrantyId: warranty.uuid,
        fieldId: el.key,
        value: el.value,
      };
      this.analyticsService.sendAnalytics(
        'toggle warranty ' + warranty.uuid + '(' + el.key + ') valore iviato: ' + el.value,
      );
      return this.httpService.post(url, data);
    });
    return combineLatest(arrCalls);
  }

  updateFieldEmail(
    changedFields: {key: string; value: unknown}[],
    email: MotorQuoteDeliveryFieldsDTO,
  ): Observable<MotorQuoteDTO[]> {
    const url = this.apiUrlService.getUrl(Endpoints.UPDATE_FIELD);
    const arrCalls = changedFields.map((el) => {
      const data = {
        fieldId: el.key,
        value: el.value,
      };
      return this.httpService.post(url, data);
    });
    return combineLatest(arrCalls);
  }

  manageWarrantyModalUpdateEmail(
    modal: NxModalRef<ModalInputsButtonsComponent, any>,
    email: MotorQuoteDeliveryFieldsDTO,
  ) {
    modal.componentInstance.formChange
      .pipe(
        switchMap((data) => this.updateFieldEmail(data, email)),
        tap((res) => {
          const last = res.reverse()[0];
          this.response = last;
        }),
      )
      .subscribe();
  }

  manageWarrantyModalUpdate(
    modal: NxModalRef<ModalInputsButtonsComponent, any>,
    warranty: MotorQuoteWarrantyDTO,
  ) {
    modal.componentInstance.formChange
      .pipe(
        switchMap((data) => this.updateField(data, warranty)),
        tap((res) => {
          const last = res.reverse()[0];
          this.response = last;
        }),
      )
      .subscribe();
  }

  noRefreshPage() {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef$),
        map((event) => (event instanceof Scroll ? event.routerEvent : event)),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      )

      .subscribe((event) => {
        if (event.url === event.urlAfterRedirects) {
          this.sessionStorageService.set('fq-url', event.url);
        }
      });
  }

  addValueParams(
    params: Record<string, string>,
    uuidObj: Record<string, Record<string, unknown>>,
  ): Record<string, unknown> {
    return Object.keys(params).reduce((obj: Record<string, unknown>, el) => {
      obj[el] =
        uuidObj[params[el]]?.['displayValue'] ?? uuidObj[params[el]]?.['value'] ?? params[el];
      return obj;
    }, {});
  }

  extractLabelValuePairs(
    data: Record<string, Record<string, unknown>>,
    uuids: string[],
    valueProps: {
      [k: string]: {
        value: string;
        label: string;
        params: Record<string, string>;
      };
    } = {},
  ): LabelValue[] {
    return uuids.reduce((arr: LabelValue[], el) => {
      const field = data[el];
      const value = valueProps?.[el]?.['value'] ?? field?.['displayValue'] ?? field?.['value'];
      if (value) {
        arr.push({
          value,
          label: valueProps?.[el]?.['label'] ?? field?.['label'],
          valueParams:
            (valueProps?.[el]?.params && this.addValueParams(valueProps?.[el]?.params, data)) ??
            undefined,
        });
      }
      return arr;
    }, []);
  }

  updatePaperDocumentationChoice(newChoice: string) {
    (<MotorQuoteDeliveryFieldsEmailDTO>this.response!.response!.insuranceData!.delivery!.fieldset[0]!.fields)!['mode'].value =
      newChoice;
    this.analyticsService.sendAnalytics(
      'switch paper documentation choice to (1:NO; 2:SI): ' + newChoice,
    );
  }

  updateFormValues(newValues: {[key: string]: any}, addressForm: FormParts): FormParts {
    return {
      ...addressForm,
      form: addressForm.form.map((field) => {
        if (typeof field.name === 'string' && field.name in newValues) {
          return {...field, value: newValues[field.name]};
        }
        return field;
      }),
    };
  }
}
